import { useCallback, useEffect, useRef, useState } from 'react';
import type { AchievementEvent, Profile } from '../shared/achievements';
import type { Session } from '../shared/types';
import { AchievementClient } from './achievementClient';
import { api } from './api';
import { useAchievementQueue } from './Achievements';

export function useAchievements(session: Session | null, online: boolean) {
  const key = session ? `${session.boardSlug ?? 'main'}:${session.id}:${session.accountId ?? ''}` : '';
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState('');
  const client = useRef<AchievementClient | null>(null);
  const identityKey=useRef(key);identityKey.current=key;
  const { enqueue, toast } = useAchievementQueue(key);
  const enqueueRef = useRef(enqueue); enqueueRef.current = enqueue;
  useEffect(() => {
    setProfile(null); setError('');
    if (!key) return;
    const next = new AchievementClient({profile:api.profile,action:api.achievementAction}, {
      profile:setProfile, error:setError, earned:ids => enqueueRef.current(ids),
    });
    client.current = next;
    void next.refresh();
    return () => { next.close(); if (client.current === next) client.current = null; };
  }, [key]);
  useEffect(() => { if (online) void client.current?.refresh(); }, [online]);
  useEffect(() => {
    if (!error || !online) return;
    const timer = window.setInterval(() => void client.current?.refresh(), 10000);
    return () => window.clearInterval(timer);
  }, [error, online]);
  const record = useCallback((event: AchievementEvent) => { void client.current?.record(event); }, []);
  const refresh = useCallback(() => { void client.current?.refresh(); }, []);
  const beforeIdentity = useCallback(async () => { await client.current?.flush(); }, []);
  const beforeMutation=useCallback(async()=>{
    const expected=client.current,scope=identityKey.current;
    await expected?.beforeMutation();
    if(expected!==client.current||scope!==identityKey.current)throw new Error('Your account changed. Try saving again with the current account.');
  },[]);
  return {profile,error,record,refresh,toast,beforeIdentity,beforeMutation};
}
